import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { purchaseOrdersAPI } from '../../lib/api';

const fmt = (iso) => (iso ? new Date(iso + 'Z').toLocaleString() : '');

export const POEmailIssuesIndicator = ({ refreshKey, autoOpen, onReview }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const autoOpened = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await purchaseOrdersAPI.getNotificationIssues();
      setData(res.data);
      if (autoOpen && res.data?.count && !autoOpened.current) {
        autoOpened.current = true;
        setOpen(true);
      }
    } catch {
      setData(null);
    }
  }, [autoOpen]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!data?.count) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs text-amber-700 hover:bg-amber-50 dark:text-amber-400"
          data-testid="po-email-issues-indicator">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span data-testid="po-email-issues-count">{data.count}</span>
          <span className="hidden sm:inline">email{data.count === 1 ? '' : 's'} need attention</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-2" data-testid="po-email-issues-list">
        <p className="px-1 pb-2 text-xs text-muted-foreground">
          Received emails that could not be sent after {data.max_attempts} automatic attempts, or may not have been delivered.
        </p>
        <div className="max-h-72 space-y-1 overflow-auto">
          {data.issues.map((i) => (
            <div key={i.notification_id} className="flex items-start gap-2 rounded border px-2 py-1.5 text-xs" data-testid={`po-email-issue-${i.notification_id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium">PO {i.po_number} · {i.status === 'uncertain' ? 'Delivery uncertain' : 'Failed'}</div>
                <div className="truncate text-muted-foreground" title={i.reason || ''}>{i.reason || 'unknown error'}</div>
                <div className="text-muted-foreground">{fmt(i.last_attempt_at || i.created_at)} · {i.attempts} attempt(s)</div>
              </div>
              <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid={`po-email-issue-review-${i.notification_id}`}
                onClick={() => { setOpen(false); onReview(i.po_id); }}>
                Review
              </Button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
